import React, { forwardRef } from 'react'

import { useTranslation } from 'react-i18next'

import {
  ButtonProps,
  Menu,
  TertiaryButton,
  useDisclosure,
} from '@enechain/ecloud-designsystem'
import {
  FaCaretDownSolid,
  FaCloudArrowDownRegular,
} from '@enechain/ecloud-icons'
import type { ListOrganizationDealPerFrameResponse } from '@enechain/eposition-proto/bffportal/v1/report_service_pb'

import { ContractListFilterState } from '~/pages/ContractList/hooks/useContractListFilter'

import type { ContractDealReportModel } from '../../models/contractDealReport'
import { AllContractsExportModal } from './AllContractsExportModal'
import { PerFrameContractsExportModal } from './PerFrameEnergyExportModal'

type ExportButtonProps = {
  data: ContractDealReportModel[] | undefined
  perFrameData: ListOrganizationDealPerFrameResponse | undefined
  filterState: ContractListFilterState
}

const createExportMenuAnchor = (
  label: string,
): React.ForwardRefExoticComponent<
  Omit<ButtonProps, 'ref' | 'children'> & React.RefAttributes<HTMLButtonElement>
> => {
  return forwardRef<HTMLButtonElement, Omit<ButtonProps, 'ref' | 'children'>>(
    function ExportMenuAnchor(props, ref) {
      return (
        <TertiaryButton
          {...props}
          ref={ref}
          leftIcon={<FaCloudArrowDownRegular />}
          rightIcon={<FaCaretDownSolid />}
          aria-label="Export Menu"
          borderRadius="base"
        >
          {label}
        </TertiaryButton>
      )
    },
  )
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  perFrameData,
  filterState,
}) => {
  const { t } = useTranslation(['page'])

  const allContractsModal = useDisclosure()
  const perFrameModal = useDisclosure()

  const menuItems = [
    {
      label: t('page:contract_list.table.export_menu.all_contracts'),
      value: 'ALL',
      onClick: (): void => {
        allContractsModal.onOpen()
      },
      disabled: data == null || data.length === 0,
    },
    {
      label: t('page:contract_list.table.export_menu.per_frame_energy'),
      value: 'PER_FRAME',
      onClick: (): void => {
        perFrameModal.onOpen()
      },
      disabled: data == null || data.length === 0,
    },
  ]

  const MenuAnchor = createExportMenuAnchor(
    t('page:contract_list.table.csv_download'),
  )

  return (
    <>
      <Menu content={menuItems}>
        <MenuAnchor disabled={data == null || data.length === 0} />
      </Menu>

      <AllContractsExportModal
        open={allContractsModal.open}
        onClose={allContractsModal.onClose}
        data={data}
        filterState={filterState}
      />

      <PerFrameContractsExportModal
        open={perFrameModal.open}
        onClose={perFrameModal.onClose}
        data={data}
        perFrameData={perFrameData}
        filterState={filterState}
      />
    </>
  )
}
